import { Box, Flex, Image, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";

// #root is display:flex, so the shell must fill or it shrink-to-fits.
export const Shell = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  padding: 14px;
  width: 100%;
`;

export const OpenInAllstarRow = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 2px 8px;
`;

export const OpenInAllstarLink = styled.a`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 12.5px;
  font-weight: 600;
  gap: 5px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

export const Panel = styled(Flex)`
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.navy900};
  border-radius: 5px;
  justify-content: center;
  overflow: hidden;
  padding: 22px 24px;
  position: relative;
`;

export const PanelScrim = styled(Box)`
  background: ${({ theme }) => theme.colors.midnightAlpha80};
  inset: 0;
  pointer-events: none;
  position: absolute;
`;

export const PanelWash = styled(Box)<{ $tint: "envy" | "ugg" | "caution" }>`
  background: linear-gradient(
    115deg,
    ${({ $tint, theme }) => theme.colors[`${$tint}Alpha20`]},
    transparent 58%
  );
  inset: 0;
  pointer-events: none;
  position: absolute;
`;

export const CornerWordmark = styled(Image)`
  bottom: 14px;
  height: 14px;
  opacity: 0.3;
  pointer-events: none;
  position: absolute;
  right: 16px;
  width: auto;
`;

export const PanelBody = styled(Flex)<{ $center?: boolean }>`
  align-items: ${({ $center }) => ($center ? "center" : "flex-start")};
  flex-direction: column;
  gap: 16px;
  max-width: 440px;
  min-width: 0;
  position: relative;
  text-align: ${({ $center }) => ($center ? "center" : "left")};
  width: 100%;
`;

export const GameBadge = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  background: ${({ theme }) => theme.colors.chalkAlpha06};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex: none;
  height: 54px;
  justify-content: center;
  position: relative;
  width: 54px;
`;

export const GameBadgeIcon = styled(Image)`
  height: 26px;
  width: auto;
`;

export const PanelHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.12;
`;

export const PanelSubheading = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  line-height: 1.55;
  text-wrap: pretty;
`;

export const PrimaryCta = styled(Flex)<{ $ugg?: boolean }>`
  align-items: center;
  background: ${({ $ugg, theme }) =>
    $ugg ? theme.colors.ugg : theme.colors.envy};
  border: none;
  border-radius: 99px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  gap: 9px;
  padding: 11px 24px;
  text-decoration: none;

  &:hover {
    background: ${({ $ugg, theme }) =>
      $ugg ? theme.colors.uggDark : theme.colors.darkerEnvy};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const UggMark = styled(Image)`
  flex: none;
  height: 28px;
  width: auto;
`;

export const SignedOutHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.15;
`;

/* ---------- Generating ---------- */

/* Indeterminate on purpose: the create call is fire-and-forget, so any percentage we
   drew would be invented. */
const slide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(320%); }
`;

export const IndeterminateTrack = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 99px;
  height: 3px;
  max-width: 240px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

export const IndeterminateFill = styled(Box)`
  animation: ${slide} 1.5s ease-in-out infinite;
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 99px;
  height: 100%;
  position: absolute;
  width: 32%;
`;

/* ---------- Unsupported ---------- */

export const UnsupportedPanel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade400};
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 9px;
  flex-direction: column;
  gap: 9px;
  justify-content: center;
  padding: 52px 20px;
  text-align: center;
`;

export const UnsupportedHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 700;
`;

export const UnsupportedBody = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12.5px;
  line-height: 1.55;
  max-width: 380px;
  text-wrap: pretty;
`;

/* ---------- Ready (clip meta beneath the player) ---------- */

// video.js does not size to an aspect-ratio parent; these overrides make it fill.
export const PlayerFrame = styled(Box)`
  /* True 16/9: stretching to fill a taller frame would either letterbox the clip or
     crop the sides off the gameplay. Slack belongs to whatever height U.GG picks. */
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.black};
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:not(:has(:fullscreen)) {
    .vjs-tech {
      border-radius: 5px;
      height: 100% !important;
      object-fit: cover;
      width: 100% !important;
    }

    /* Absolute against the frame, not 100% of the wrappers: EmbeddablePlayer nests the
       player three heightless divs deep, so a percentage collapses the video to 0px. */
    .video-js {
      background: transparent;
      height: 100% !important;
      inset: 0;
      position: absolute !important;
      width: 100% !important;
    }
  }

  .player-component {
    height: 100%;
    position: relative;
  }
`;

export const ClipMetaRow = styled(Flex)`
  align-items: center;
  gap: 10px;
  padding: 0 2px;
`;

export const ClipAvatar = styled(Box)`
  background: ${({ theme }) => theme.colors.navy100};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex: none;
  height: 30px;
  overflow: hidden;
  width: 30px;
`;

export const ClipAvatarImage = styled(Image)`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

export const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14.5px;
  font-weight: 700;
  line-height: 1.2;
`;

export const ClipByline = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
`;

// ThumbnailWrapper is flex:1;min-height:0, so the cells need a definite height here.
export const ClipRail = styled(Box)`
  display: flex;
  height: 132px;
  min-height: 0;
`;

export const ReadyStack = styled(Flex)`
  flex-direction: column;
  gap: 11px;
  height: 100%;
  justify-content: center;
  min-height: 0;
`;
